import { useMemo } from 'react';

import {
  AlarmActivation,
  IconConfig,
  IconConfigVariant,
  Severity,
} from '@repo/sdk/models/components';

import { useDeviceAlarmActivations } from '@/hooks/query-hooks/alarm-activations';

type ActiveDeviceAlarms = {
  critical: AlarmActivation[];
  warning: AlarmActivation[];
  caution: AlarmActivation[];
};

const DEFAULT_ALARMS: ActiveDeviceAlarms = {
  critical: [],
  warning: [],
  caution: [],
};

/**
 * Determines if an alarm activation is currently active based on its severity.
 * Critical alarms are active if not cleared; Warning/Caution alarms are active if not resolved.
 */
const isActiveAlarm = (activation: AlarmActivation): boolean => {
  switch (activation.severity) {
    case Severity.Critical:
      // Critical alarms are active if they have not been cleared
      return !activation.clearedAt;
    case Severity.Warning:
    case Severity.Caution:
      // Warning and caution alarms are active if they have not been resolved
      return !activation.resolvedAt;
    default:
      return false;
  }
};

/**
 * Returns the active device alarms grouped by severity
 * @param deviceAlarmActivations - The alarm activations for the device
 * @returns The active device alarms grouped by severity
 **/
const getActiveDeviceAlarms = (
  deviceAlarmActivations: AlarmActivation[] | undefined
): ActiveDeviceAlarms => {
  if (!deviceAlarmActivations) return DEFAULT_ALARMS;

  const activeAlarms = deviceAlarmActivations.filter(isActiveAlarm);

  return activeAlarms.reduce<ActiveDeviceAlarms>(
    (acc, activation) => {
      const key = activation.severity;
      if (!acc[key as keyof ActiveDeviceAlarms]) {
        acc[key as keyof ActiveDeviceAlarms] = [];
      }
      acc[key as keyof ActiveDeviceAlarms].push(activation);
      return acc;
    },
    { critical: [], warning: [], caution: [] }
  );
};

/**
 * Returns the tooltip content for the device alarms
 * @param deviceAlarms - The active device alarms
 * @returns The tooltip content for the device alarms
 */
const getAlarmTooltipContent = (deviceAlarms: ActiveDeviceAlarms): string => {
  const allAlarms = [...deviceAlarms.critical, ...deviceAlarms.warning, ...deviceAlarms.caution];
  if (allAlarms.length === 0) {
    return '';
  }
  return allAlarms.map((alarm) => `${alarm.severity.toUpperCase()}: ${alarm.message}`).join('\n');
};

/**
 * Returns the severity of the device alarms based on the active alarms
 * @param deviceAlarms - The active device alarms
 * @returns The severity of the device alarms
 */
const getDeviceAlarmSeverity = (deviceAlarms: ActiveDeviceAlarms): Severity | undefined => {
  return deviceAlarms.critical.length > 0
    ? Severity.Critical
    : deviceAlarms.warning.length > 0
      ? Severity.Warning
      : deviceAlarms.caution.length > 0
        ? Severity.Caution
        : undefined;
};

/**
 * Returns the icon config for the device alarms based on the active alarms
 * @param deviceId - The ID of the device
 * @returns The icon config for the device alarms
 */
export const useDeviceAlarmIconConfig = (deviceId: string): IconConfig | undefined => {
  const deviceAlarms = useDeviceAlarmActivations({ deviceId });
  // Return a BFF IconConfig object if there are any alarms, else return undefined
  const alarmIconConfig: IconConfig | undefined = useMemo(() => {
    const activeDeviceAlarms = getActiveDeviceAlarms(deviceAlarms);
    const deviceAlarmSeverity = getDeviceAlarmSeverity(activeDeviceAlarms);
    if (!deviceAlarmSeverity) {
      return undefined;
    }

    const alarmTooltipContent = getAlarmTooltipContent(activeDeviceAlarms);
    let variant: IconConfigVariant = IconConfigVariant.Default;
    switch (deviceAlarmSeverity) {
      case Severity.Critical:
        variant = IconConfigVariant.Critical;
        break;
      case Severity.Warning:
        variant = IconConfigVariant.Warning;
        break;
      case Severity.Caution:
        variant = IconConfigVariant.Caution;
        break;
    }
    return {
      icon: 'Bell',
      variant,
      tooltipContent: { value: alarmTooltipContent },
      isPulsing: true,
    };
  }, [deviceAlarms]);

  return alarmIconConfig;
};
